import { keepPreviousData } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, Download, Search, UserRoundCog } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { FacetChips } from '@/components/molecules/FacetChips';
import { FilterSelect } from '@/components/molecules/FilterSelect';
import { RESULT_STYLE, ResultPill } from '@/components/molecules/ResultPill';
import { useModuleFilter } from '@/context/ModuleFilter';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useUsd } from '@/hooks/useUsd';
import type { ConversationListItem, ConversationResult } from '@/schemas/commons';
import type { ConversationsParams } from '@/services/Conversations';

import {
	ALERT_OPTIONS,
	ALERT_SEVERITY,
	AREA_OPTIONS,
	CHANNEL_OPTIONS,
	COUNTRY_OPTIONS,
	DEFAULT_ALERT_SEVERITY,
	PAGE_SIZE,
	PERIOD_OPTIONS,
	RESULT_OPTIONS,
	STATUS_OPTIONS,
} from './Conversations.constants';
import { ConversationsLoading } from './Conversations.loading';
import { useConversations } from './Conversations.requests';
import { styles } from './Conversations.styles';
import type { ConversationRowProps, FilterKey, FilterState } from './Conversations.types';

export const Conversations = () => {
	const usd = useUsd();
	const { t } = useTranslation('translation', { keyPrefix: 'conversations' });
	const { t: tResult } = useTranslation('translation', { keyPrefix: 'resultPill' });
	const { selectedModule } = useModuleFilter();
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const [filters, setFilters] = useState<FilterState>({});
	const debouncedSearch = useDebouncedValue(search.trim());

	// Every filter is server-side: the rows *and* `totalItems` come back
	// narrowed, so the pager keeps telling the truth instead of paging over a
	// set the filter already rejected. An unset filter is simply absent from
	// the query string.
	const params: ConversationsParams = {
		page,
		pageSize: PAGE_SIZE,
		moduleName: selectedModule,
		...(debouncedSearch ? { search: debouncedSearch } : {}),
		...filters,
	};
	const { data, isLoading, isError } = useConversations(params, { placeholderData: keepPreviousData });

	const onSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	/** Set from a dropdown. The empty string clears the filter. */
	const onFilterChange = (key: FilterKey, value: string) => {
		setFilters((prev) => ({ ...prev, [key]: value || undefined }));
		setPage(1);
	};

	/** Set from a chip in the table — same filter, toggled. */
	const onFilterToggle = (key: FilterKey, value: string) => {
		setFilters((prev) => ({ ...prev, [key]: prev[key] === value ? undefined : value }));
		setPage(1);
	};

	const clearFilters = () => {
		setSearch('');
		setFilters({});
		setPage(1);
	};

	const areaLabel = (value: string) => t(`AREA_${value.toUpperCase()}`, { defaultValue: value });
	const alertLabel = (value: string) => t(`ALERT_${value.toUpperCase()}`, { defaultValue: value });
	const statusLabel = (value: string) => t(`STATUS_${value.toUpperCase()}`, { defaultValue: value });
	// Options come from the same enum ResultPill maps, so every value has a label key.
	const resultLabel = (value: string) => tResult(RESULT_STYLE[value as ConversationResult].labelKey);
	const periodLabel = (value: string) => t(`FILTER_PERIOD_${value.toUpperCase()}`, { defaultValue: value });

	if (isLoading) return <ConversationsLoading />;
	if (isError || !data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const { items, pagination, totals } = data;
	const from = (pagination.page - 1) * pagination.pageSize + 1;
	const to = Math.min(pagination.page * pagination.pageSize, pagination.totalItems);

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
				<div className="flex items-center gap-xs">
					<button
						type="button"
						className="inline-flex items-center gap-3xs text-[11px] text-subtle transition-colors hover:text-default"
					>
						<Download className="h-sm w-sm" />
						{t('EXPORT_CSV')}
					</button>
				</div>
			</header>

			<main className={styles.content}>
				<header className="flex flex-wrap items-end justify-between gap-md">
					<div>
						<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
						<h1 className={styles.pageH1}>{t('TITLE')}</h1>
						<p className={styles.pageSub}>{t('SUBTITLE', { count: pagination.totalItems })}</p>
					</div>
					<p className="text-[12px] text-subtle">
						{t('PERIOD_COST')}{' '}
						<span className="font-semibold tabular-nums text-default">{usd.total(totals.costUsdPeriod)}</span>
						<span className="mx-2xs text-subtle">·</span>
						{t('PAGE_COST')}{' '}
						<span className="font-semibold tabular-nums text-default">{usd.total(totals.costUsdFiltered)}</span>
					</p>
				</header>

				<section className={styles.filterRow}>
					<div className="relative max-w-[288px] flex-1">
						<Search className="pointer-events-none absolute left-xs top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-subtle" />
						<input
							type="search"
							value={search}
							onChange={(e) => onSearchChange(e.target.value)}
							placeholder={t('SEARCH_PLACEHOLDER')}
							aria-label={t('SEARCH_ARIA')}
							className={styles.filterInput}
						/>
					</div>
					<FilterSelect
						value={filters.channel ?? ''}
						onChange={(value) => onFilterChange('channel', value)}
						options={CHANNEL_OPTIONS}
						allLabel={t('FILTER_ALL_CHANNELS')}
						optionLabel={(value) => value}
						ariaLabel={t('FILTER_CHANNEL_ARIA')}
					/>
					<FilterSelect
						value={filters.country ?? ''}
						onChange={(value) => onFilterChange('country', value)}
						options={COUNTRY_OPTIONS}
						allLabel={t('FILTER_ALL_COUNTRIES')}
						optionLabel={(value) => value}
						ariaLabel={t('FILTER_COUNTRY_ARIA')}
					/>
					<FilterSelect
						value={filters.period ?? ''}
						onChange={(value) => onFilterChange('period', value)}
						options={PERIOD_OPTIONS}
						allLabel={t('FILTER_ALL_PERIODS')}
						optionLabel={periodLabel}
						ariaLabel={t('FILTER_PERIOD_ARIA')}
					/>
					<FilterSelect
						value={filters.status ?? ''}
						onChange={(value) => onFilterChange('status', value)}
						options={STATUS_OPTIONS}
						allLabel={t('FILTER_ALL_STATUSES')}
						optionLabel={statusLabel}
						ariaLabel={t('FILTER_STATUS_ARIA')}
					/>
					<FilterSelect
						value={filters.result ?? ''}
						onChange={(value) => onFilterChange('result', value)}
						options={RESULT_OPTIONS}
						allLabel={t('FILTER_ALL_RESULTS')}
						optionLabel={resultLabel}
						ariaLabel={t('FILTER_RESULT_ARIA')}
					/>
					<FilterSelect
						value={filters.area ?? ''}
						onChange={(value) => onFilterChange('area', value)}
						options={AREA_OPTIONS}
						allLabel={t('FILTER_ALL_AREAS')}
						optionLabel={areaLabel}
						ariaLabel={t('FILTER_AREA_ARIA')}
					/>
					<FilterSelect
						value={filters.alert ?? ''}
						onChange={(value) => onFilterChange('alert', value)}
						options={ALERT_OPTIONS}
						allLabel={t('FILTER_ALL_ALERTS')}
						optionLabel={alertLabel}
						ariaLabel={t('FILTER_ALERT_ARIA')}
					/>
					<button
						type="button"
						onClick={clearFilters}
						className="ml-auto text-[11px] text-subtle transition-colors hover:text-default"
					>
						{t('CLEAR_FILTERS')}
					</button>
				</section>

				<section>
					<header className="mb-xs flex items-baseline justify-between">
						<div className="text-[12px] text-subtle">
							{t('SHOWING')}{' '}
							<span className="font-medium tabular-nums text-default">
								{from}–{to}
							</span>{' '}
							{t('OF')} <span className="font-medium tabular-nums text-default">{pagination.totalItems}</span>
						</div>
					</header>

					<table className="w-full text-left text-[13px]">
						<thead className="border-b border-subtle">
							<tr>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_THREAD')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_USER')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_COMPANY')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_COUNTRY')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs text-right`}>{t('COL_MESSAGES')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs text-right`}>{t('COL_COST')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_ACTIVITY')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_AREA')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_ALERT')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_STATUS')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_RESULT')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_EVALUATION')}</th>
							</tr>
						</thead>
						<tbody>
							{items.map((conv) => (
								<ConversationRow key={conv.threadId} conv={conv} onFacetToggle={onFilterToggle} />
							))}
						</tbody>
					</table>

					<footer className="mt-sm flex items-center justify-between border-t border-subtle pt-sm">
						<div className="text-[11px] text-subtle">
							{t('PAGE_SUBTOTAL')}{' '}
							<span className="font-medium tabular-nums text-default">{usd.total(totals.costUsdFiltered)}</span>
							<span className="mx-2xs text-subtle">·</span>
							{t('PAGE_OF', { page: pagination.page, totalPages: pagination.totalPages })}
						</div>
						<nav className="flex items-center gap-3xs">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page <= 1}
								className={styles.pageBtn}
								aria-label={t('PREV')}
							>
								<ChevronLeft className="h-[14px] w-[14px]" />
							</button>
							{Array.from({ length: Math.min(3, pagination.totalPages) }).map((_, i) => {
								const p = i + 1;

								return (
									<button
										key={p}
										type="button"
										onClick={() => setPage(p)}
										className={page === p ? styles.pageBtnActive : styles.pageBtn}
									>
										{p}
									</button>
								);
							})}
							{pagination.totalPages > 4 && <span className="px-2xs text-[11px] text-subtle">…</span>}
							{pagination.totalPages > 3 && (
								<button
									type="button"
									onClick={() => setPage(pagination.totalPages)}
									className={page === pagination.totalPages ? styles.pageBtnActive : styles.pageBtn}
								>
									{pagination.totalPages}
								</button>
							)}
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
								disabled={page >= pagination.totalPages}
								className={styles.pageBtn}
								aria-label={t('NEXT')}
							>
								<ChevronRight className="h-[14px] w-[14px]" />
							</button>
						</nav>
					</footer>
				</section>
			</main>
		</>
	);
};

const ConversationRow = ({ conv, onFacetToggle }: ConversationRowProps) => {
	const usd = useUsd();
	const { t } = useTranslation('translation', { keyPrefix: 'conversations' });
	const areaLabel = (value: string) => t(`AREA_${value.toUpperCase()}`, { defaultValue: value });
	const alertLabel = (value: string) => t(`ALERT_${value.toUpperCase()}`, { defaultValue: value });

	return (
		<tr className="relative border-b border-subtle/50 transition-colors hover:bg-grey-25">
			<td className="px-sm py-xs">
				<Link to={`/conversations/${conv.threadId}`} className="after:absolute after:inset-0 after:content-['']">
					<code className="font-mono text-[11px] text-default">{conv.threadId}</code>
				</Link>
			</td>
			<td className="px-sm py-xs">
				<div className="flex items-center gap-2xs">
					<span className={styles.userAvatar}>{conv.userInitials}</span>
					{conv.userName}
				</div>
			</td>
			<td className={`${styles.tableCellMuted} px-sm py-xs`}>
				{conv.companyName}
				{conv.companyId && conv.companyId !== conv.companyName && (
					<span className={styles.companyId}>{conv.companyId}</span>
				)}
			</td>
			<td className={`${styles.tableCellMuted} px-sm py-xs`}>{conv.country}</td>
			<td className={`${styles.tableCell} px-sm py-xs text-right tabular-nums`}>{conv.messageCount}</td>
			<td
				className={`px-sm py-xs text-right tabular-nums ${
					conv.status === 'tool_error' ? 'font-semibold text-error' : styles.tableCell
				}`}
			>
				{usd.unit(conv.costUsd)}
			</td>
			<td className={`${styles.tableCellMuted} px-sm py-xs tabular-nums`}>{conv.lastActivity}</td>
			<td className="px-sm py-xs">
				<FacetChips
					values={conv.consultationAreas}
					chipClassName={styles.areaChip}
					label={areaLabel}
					title={(value) => t('FILTER_BY', { value: areaLabel(value) })}
					emptyLabel={t('EMPTY_VALUE')}
					onSelect={(value) => onFacetToggle('area', value)}
				/>
			</td>
			<td className="px-sm py-xs">
				<FacetChips
					values={conv.alerts}
					chipClassName={(value) => styles.alertChip[ALERT_SEVERITY[value] ?? DEFAULT_ALERT_SEVERITY]}
					label={alertLabel}
					title={(value) => t('FILTER_BY', { value: alertLabel(value) })}
					emptyLabel={t('EMPTY_VALUE')}
					onSelect={(value) => onFacetToggle('alert', value)}
				/>
			</td>
			<td className="px-sm py-xs">
				{conv.status === 'active' && (
					<span className={styles.statusActive}>
						<span className={styles.dotSuccess} />
						{t('STATUS_ACTIVE')}
					</span>
				)}
				{conv.status === 'closed' && <span className={styles.statusClosed}>{t('STATUS_CLOSED')}</span>}
				{conv.status === 'handoff' && (
					<span className={styles.statusHandoff}>
						<UserRoundCog className="h-sm w-sm" />
						{t('STATUS_HANDOFF')}
					</span>
				)}
				{conv.status === 'tool_error' && (
					<span className={styles.statusError}>
						<AlertTriangle className="h-sm w-sm" />
						{t('STATUS_TOOL_ERROR')}
					</span>
				)}
			</td>
			<td className="px-sm py-xs">
				<ResultPill result={conv.result} />
			</td>
			<td className="px-sm py-xs">
				<EvaluationSummaryBadge summary={conv.evaluationSummary} />
			</td>
		</tr>
	);
};

const EvaluationSummaryBadge = ({ summary }: { summary: ConversationListItem['evaluationSummary'] }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversations' });

	if (!summary || summary.status === 'not_applicable')
		return <span className="text-[11px] text-subtle">{t('EVAL_NOT_APPLICABLE')}</span>;
	if (summary.status === 'pending')
		return (
			<span className="inline-flex items-center gap-3xs rounded-full bg-grey-25 px-2xs py-3xs text-[10px] text-subtle">
				{t('EVAL_PENDING')}
			</span>
		);
	const failed = summary.totalJudges - summary.passedJudges;

	return (
		<span
			className={`inline-flex items-center gap-3xs rounded-full px-2xs py-3xs text-[10px] font-medium tabular-nums ${
				failed > 0 ? 'bg-red-50 text-error' : 'bg-green-50 text-success'
			}`}
		>
			{t('EVAL_RATIO', { passed: summary.passedJudges, total: summary.totalJudges })}
		</span>
	);
};
